import { format, isValid, parse, startOfDay } from 'date-fns';
import { CalendarIcon, LucideCircleX } from 'lucide-react';
import React, {
  ChangeEvent,
  FocusEvent,
  KeyboardEvent,
  MouseEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import DatePicker from './DatePicker';

export interface DatePickerInputProps {
  date?: string | Date | { start: Date | null; end: Date | null } | null;
  setDate?: (
    date: Date | { start: Date | null; end: Date | null } | string | null,
  ) => void;
  formatStr?: string;
  placeholder?: string;
  classNamePlaceholder?: string;
  classNameIcon?: string;
  maxDate?: Date;
  minDate?: Date;
  maxDateErrorMessage?: string;
  minDateErrorMessage?: string;
  hasError?: boolean;
  errorMessage?: string;
  readOnly?: boolean;
  inputReadOnly?: boolean;
  className?: string;
  wrapperClassName?: string;
  hideIcon?: boolean;
  mode?: 'single' | 'range';
  timeZone?: number;
  onBlurDateInput?: () => void;
  classNameText?: string;
  classNameBtn?: string;
  isShowBtnReset?: boolean;
  isShowBtnFooter?: boolean;
  pickerMode?: 'days' | 'months' | 'years';
  classNameErrorMessage?: string;
  name?: string;
  resetErrorKey?: string | number;
  displayValue?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

function isValidDateString(dateString: string): boolean {
  return isValid(new Date(dateString));
}

function maskByFormat(value: string, formatStr: string) {
  const digits = value.replace(/\D/g, '');
  const parts = formatStr.split(/[^A-Za-z]/);
  const separators = formatStr.match(/[^A-Za-z]/g) ?? [];
  let cursor = 0;

  return parts
    .map((part, index) => {
      const next = digits.slice(cursor, cursor + part.length);
      cursor += part.length;
      if (!next) return '';
      return `${next}${
        next.length === part.length && separators[index]
          ? separators[index]
          : ''
      }`;
    })
    .join('')
    .replace(/[^0-9]$/, '');
}

export default function DatePickerInput({
  date,
  setDate,
  formatStr,
  placeholder,
  maxDate,
  minDate,
  maxDateErrorMessage,
  minDateErrorMessage,
  hasError,
  errorMessage,
  readOnly,
  inputReadOnly,
  className,
  wrapperClassName,
  hideIcon,
  classNamePlaceholder,
  mode = 'single',
  timeZone = 0,
  onBlurDateInput,
  classNameText,
  classNameIcon,
  classNameBtn,
  isShowBtnReset = true,
  isShowBtnFooter = true,
  pickerMode = 'days',
  classNameErrorMessage,
  name,
  resetErrorKey,
  displayValue,
  open: controlledOpen,
  onOpenChange,
}: DatePickerInputProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = (nextOpen: boolean | ((open: boolean) => boolean)) => {
    const resolvedOpen =
      typeof nextOpen === 'function' ? nextOpen(open) : nextOpen;
    if (controlledOpen === undefined) {
      setInternalOpen(resolvedOpen);
    }
    onOpenChange?.(resolvedOpen);
  };
  const { t } = useTranslation();
  const formatMap = {
    days: formatStr ?? 'dd/MM/yyyy',
    months: 'MM/yyyy',
    years: 'yyyy',
  };
  const dateFmt = formatMap[pickerMode].split(' ')[0];
  const inputRef = useRef<HTMLInputElement>(null);
  const pendingDateRef = useRef<
    Date | { start: Date | null; end: Date | null } | string | null | undefined
  >(undefined);

  let displayText = '';
  let datePickerValue: Date | { start: Date; end: Date } | undefined;
  if (
    mode === 'range' &&
    date &&
    typeof date === 'object' &&
    'start' in date &&
    'end' in date
  ) {
    const { start, end } = date;
    if (start && end) {
      datePickerValue = { start, end };
      displayText = `${format(start, formatMap[pickerMode])} - ${format(
        end,
        formatMap[pickerMode],
      )}`;
    } else if (start) {
      displayText = `${format(start, formatMap[pickerMode])} - ...`;
    }
  } else if (date instanceof Date) {
    datePickerValue = date;
    displayText = format(date, formatMap[pickerMode]).toUpperCase();
  } else if (typeof date === 'string' && isValidDateString(date)) {
    const utcDate = new Date(date);
    const zoneDate = new Date(utcDate.getTime() + timeZone * 60 * 60 * 1000);
    datePickerValue = new Date(zoneDate);
    displayText = format(zoneDate, formatMap[pickerMode]).toUpperCase();
  }

  if (displayValue && displayText) {
    displayText = displayValue;
  }

  const [draft, setDraft] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isInternalDirty, setIsInternalDirty] = useState(false);
  const shownValue = draft ?? displayText;
  const maxDateDefaultErrorMessage = maxDate
    ? t('common.dateAfterMax', { date: format(maxDate, dateFmt) })
    : null;
  const minDateDefaultErrorMessage = minDate
    ? t('b2b.dateBeforeMin', { date: format(minDate, dateFmt) })
    : null;
  const displayedError =
    error === maxDateDefaultErrorMessage && maxDateErrorMessage
      ? maxDateErrorMessage
      : error === minDateDefaultErrorMessage && minDateErrorMessage
      ? minDateErrorMessage
      : error;
  const shouldShowDisplayedError = isInternalDirty && !!displayedError;

  useEffect(() => {
    setDraft(null);
    setError(null);
    setIsInternalDirty(false);
    pendingDateRef.current = undefined;
  }, [resetErrorKey]);

  const isTextInputReadOnly = inputReadOnly || mode === 'range';
  const openOnInputClick = !readOnly && (isTextInputReadOnly || !!hideIcon);

  const validateInput = (
    raw: string,
  ): { value: Date | string | null } | { message: string } => {
    const text = raw.trim();
    if (!text) return { value: null };
    const parsed = parse(text, dateFmt, new Date());
    if (!isValid(parsed) || format(parsed, dateFmt) !== text)
      return { message: t('common.invalidDate', 'Invalid date') };
    const clean =
      pickerMode === 'months'
        ? new Date(parsed.getFullYear(), parsed.getMonth(), 1)
        : new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
    if (minDate && startOfDay(clean) < startOfDay(minDate))
      return {
        message: minDateErrorMessage ?? minDateDefaultErrorMessage ?? '',
      };
    if (maxDate && startOfDay(clean) > startOfDay(maxDate))
      return {
        message: maxDateErrorMessage ?? maxDateDefaultErrorMessage ?? '',
      };
    return {
      value: pickerMode === 'years' ? clean.getFullYear().toString() : clean,
    };
  };

  const commitInput = (raw: string): boolean => {
    const result = validateInput(raw);
    if ('message' in result) {
      setError(result.message);
      return false;
    }
    setError(null);
    if (result.value === null) {
      if (datePickerValue) setDate?.(null);
    } else {
      setDate?.(result.value);
    }
    setDraft(null);
    return true;
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (isTextInputReadOnly) return;
    setIsInternalDirty(true);
    const el = e.target;
    const caret = el.selectionStart ?? el.value.length;
    const digitsBeforeCaret = el.value
      .slice(0, caret)
      .replace(/\D/g, '').length;
    const masked = maskByFormat(el.value, dateFmt);
    setDraft(masked);
    if (error) setError(null);

    requestAnimationFrame(() => {
      const input = inputRef.current;
      if (!input) return;
      if (input.value !== masked) input.value = masked;
      let pos = 0;
      let seen = 0;
      while (pos < masked.length && seen < digitsBeforeCaret) {
        if (/\d/.test(masked[pos])) seen++;
        pos++;
      }
      input.setSelectionRange(pos, pos);
    });
  };

  const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (isTextInputReadOnly) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      if (commitInput(e.currentTarget.value)) setOpen(false);
    }
  };

  const handleInputBlur = (e: FocusEvent<HTMLInputElement>) => {
    if (!readOnly && mode !== 'range' && draft !== null)
      commitInput(e.target.value);
    onBlurDateInput?.();
  };

  const handleClear = (e: MouseEvent) => {
    e.stopPropagation();
    setDraft(null);
    setError(null);
    setIsInternalDirty(true);
    setDate?.('');
  };

  const handleChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      if (pendingDateRef.current !== undefined) {
        setDate?.(pendingDateRef.current);
        pendingDateRef.current = undefined;
      }
      onBlurDateInput?.();
    }
  };

  return (
    <div
      className={cn('relative min-w-0 w-full', wrapperClassName)}
      onClick={e => e.stopPropagation()}
    >
      <Popover
        open={open && !readOnly}
        onOpenChange={handleChange}
        modal={false}
      >
        <PopoverAnchor asChild>
          <div
            data-field-name={name}
            onClick={e => {
              if (readOnly || mode === 'range') return;
              if (openOnInputClick) setOpen(prev => !prev);
              if (!(e.target as HTMLElement).closest('button'))
                inputRef.current?.focus();
            }}
            className={cn(
              'group flex h-10 w-full min-w-0 items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2',
              !readOnly && 'hover:border-gray-300 hover:bg-white',
              className,
              classNameBtn,
              hasError || shouldShowDisplayedError
                ? 'border-[#f06f24]'
                : !readOnly && 'focus-within:border-[#f06f24]',
            )}
          >
            {readOnly ? (
              <span className={cn('text-sm text-gray-500', classNameText)}>
                {displayText || (
                  <span className={cn('text-gray-400', classNamePlaceholder)}>
                    {placeholder ?? formatStr}
                  </span>
                )}
              </span>
            ) : (
              <input
                ref={inputRef}
                type="text"
                name={name}
                value={shownValue}
                readOnly={isTextInputReadOnly}
                tabIndex={0}
                autoComplete="off"
                inputMode="numeric"
                placeholder={placeholder ?? formatStr}
                onChange={handleInputChange}
                onKeyDown={handleInputKeyDown}
                onBlur={handleInputBlur}
                onClick={() => {
                  if (openOnInputClick) setOpen(true);
                }}
                className={cn(
                  'min-w-0 flex-1 border-0 bg-transparent p-0 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus-visible:ring-0',
                  isTextInputReadOnly ? 'cursor-pointer' : 'cursor-text',
                  classNameText,
                  classNamePlaceholder,
                )}
              />
            )}
            {!readOnly && (
              <span className="flex shrink-0 items-center gap-1">
                {shownValue && (
                  <button
                    type="button"
                    aria-label="clear"
                    tabIndex={-1}
                    onMouseDown={e => e.preventDefault()}
                    onClick={handleClear}
                    className="inline-flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent p-0 leading-none text-gray-400 shadow-none outline-none hover:bg-gray-100 hover:text-gray-700"
                  >
                    <LucideCircleX width={14} height={14} strokeWidth={2} />
                  </button>
                )}
                {!hideIcon && (
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      aria-label="open calendar"
                      className="inline-flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-md border-0 bg-gray-50 p-0 leading-none text-gray-600 shadow-none outline-none hover:bg-[#fff7e6] hover:text-[#f06f24]"
                    >
                      <CalendarIcon
                        width={17}
                        height={17}
                        className={classNameIcon}
                      />
                    </button>
                  </PopoverTrigger>
                )}
              </span>
            )}
          </div>
        </PopoverAnchor>
        <PopoverContent
          align="start"
          side="bottom"
          sideOffset={8}
          collisionPadding={16}
          className="z-[1000] max-h-[min(520px,calc(100vh-32px))] w-auto max-w-[calc(100vw-32px)] overflow-auto border-0 bg-transparent p-0 shadow-none"
        >
          <DatePicker
            value={datePickerValue}
            onChange={nextDate => {
              pendingDateRef.current = nextDate;
              setDraft(null);
              setError(null);
              setIsInternalDirty(true);
            }}
            maxDate={maxDate}
            minDate={minDate}
            pickerMode={pickerMode}
            mode={mode}
            onClose={() => handleChange(false)}
            isShowBtnReset={isShowBtnReset}
            isShowBtnFooter={isShowBtnFooter}
          />
        </PopoverContent>
      </Popover>
      {!readOnly && (shouldShowDisplayedError || hasError) && (
        <p
          className={cn(
            'absolute left-0 top-full mt-1 text-xs leading-4 text-[#ff4d4f]',
            classNameErrorMessage,
          )}
        >
          {shouldShowDisplayedError ? displayedError : errorMessage}
        </p>
      )}
    </div>
  );
}
